/**
 * Runtime Environment Detection
 * 
 * Centralized utility to detect whether the app is running in production,
 * preview, or local environments. All tracking decisions flow through here.
 * 
 * Production hosts: www.elevadvisor.com, elevadvisor.com
 * Everything else is considered non-production.
 */

export type RuntimeEnvironment = 'production' | 'preview' | 'local' | 'unknown';

const PRODUCTION_HOSTS = new Set([
  'www.elevadvisor.com',
  'elevadvisor.com',
]);

const LOCAL_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
]);

const PREVIEW_PATTERNS = [
  'lovable.app',
  'lovable.dev',
  'lovableproject.com',
  'preview',
  'staging',
];

/**
 * Get the current runtime environment based on hostname.
 */
export function getRuntimeEnvironment(): RuntimeEnvironment {
  if (typeof window === 'undefined') return 'unknown';

  const host = window.location.hostname;

  if (PRODUCTION_HOSTS.has(host)) return 'production';
  if (LOCAL_HOSTS.has(host)) return 'local';
  if (PREVIEW_PATTERNS.some(p => host.includes(p))) return 'preview';

  return 'unknown';
}

/** Returns true only for canonical production domains. */
export function isProductionHost(): boolean {
  return getRuntimeEnvironment() === 'production';
}

/** Returns true for preview/staging/lovable domains. */
export function isPreviewHost(): boolean {
  return getRuntimeEnvironment() === 'preview';
}

/** Returns true for localhost / 127.0.0.1. */
export function isLocalHost(): boolean {
  return getRuntimeEnvironment() === 'local';
}

/**
 * Master gate for all tracking, analytics, and third-party scripts.
 * Returns true ONLY on production hosts.
 */
export function shouldEnableTracking(): boolean {
  return isProductionHost();
}
